import { fetchListingsResult, deriveFeedState, MAX_PAGE_SIZE, BrowseStatus } from '@/lib/marketplace';
import { Listing } from '@/components/JobCard';

/**
 * PERMANENT MOBILE BROWSE REGRESSION SUITE
 * =========================================
 * Guards the launch blocker: "51 listings in the database, Zero listings on
 * mobile". Run it from Admin -> Security after any change to the browse path,
 * the rate limiter, the masked view or the marketplace page.
 *
 * It deliberately does NOT assert a fixed listing count (the catalog changes) —
 * it compares what the UI would render against what the API actually returned.
 */

export interface RegressionCase {
  name: string;
  passed: boolean;
  detail: string;
}

/** Fields that must NEVER appear with a value in a public browse payload. */
const FORBIDDEN_FIELDS = [
  'owner_id', 'buyer_id', 'user_id', 'contact_name', 'contact_email', 'contact_phone',
  'phone', 'email', 'address', 'notes', 'internal_notes', 'documents', 'document_path',
  'signed_url', 'stripe_customer_id', 'payment_intent_id',
];

const leaks = (rows: Listing[]): string[] => {
  const found = new Set<string>();
  rows.forEach((r) => {
    const obj = r as unknown as Record<string, unknown>;
    FORBIDDEN_FIELDS.forEach((f) => {
      if (obj[f] !== undefined && obj[f] !== null && obj[f] !== '') found.add(f);
    });
    // city / zip are present but MUST be masked while the lead is locked.
    if (obj.location_locked !== false) {
      if (obj.city) found.add('city');
      if (obj.zip) found.add('zip');
    }
  });
  return [...found];
};

const pass = (name: string, detail: string): RegressionCase => ({ name, passed: true, detail });
const fail = (name: string, detail: string): RegressionCase => ({ name, passed: false, detail });

/**
 * Pure state-machine checks. These run with no network and catch the exact class
 * of bug that caused the outage: a failure being rendered as an empty catalog.
 */
export const runStateMachineCases = (): RegressionCase[] => {
  const cases: RegressionCase[] = [];

  cases.push(
    deriveFeedState('ok', 51) === 'ok'
      ? pass('API returns rows -> renders listings', '51 rows -> "ok"')
      : fail('API returns rows -> renders listings', 'listings present but UI would not render them'),
  );

  cases.push(
    deriveFeedState('ok', 0) === 'empty'
      ? pass('Genuine zero results -> empty state', 'only a successful 0-row response shows "empty"')
      : fail('Genuine zero results -> empty state', 'unexpected state'),
  );

  const failureModes: BrowseStatus[] = ['rate_limited', 'network_error', 'server_error'];
  const wrong = failureModes.filter((s) => deriveFeedState(s, 0) === 'empty');
  cases.push(
    wrong.length === 0
      ? pass('Failures never render "Zero listings"', 'rate_limited / network_error / server_error keep their own message + Retry')
      : fail('Failures never render "Zero listings"', `these were shown as empty: ${wrong.join(', ')}`),
  );

  return cases;
};

/**
 * Live checks against the production browse path, using the SAME payload the
 * mobile client sends.
 */
export const runLiveCases = async (): Promise<RegressionCase[]> => {
  const cases: RegressionCase[] = [];

  // 1. Default mobile request — no filters, page 0. This is the exact call the
  //    marketplace makes on first paint.
  const first = await fetchListingsResult({ limit: MAX_PAGE_SIZE, offset: 0 });
  if (first.status !== 'ok') {
    cases.push(fail('Default feed loads', `status="${first.status}" (retryAfter ${first.retryAfter}s)`));
    return cases;
  }
  cases.push(pass('Default feed loads', `${first.listings.length} listing(s) returned with no filters applied`));

  // 2. UI must agree with the API.
  const state = deriveFeedState(first.status, first.listings.length);
  cases.push(
    first.listings.length > 0 && state === 'ok'
      ? pass('UI renders what the API returned', `${first.listings.length} card(s) would render`)
      : first.listings.length === 0
        ? pass('UI renders what the API returned', 'API genuinely returned 0 rows')
        : fail('UI renders what the API returned', 'API returned rows but the UI state was not "ok"'),
  );

  // 3. Server-side row cap still enforced.
  const over = await fetchListingsResult({ limit: 500, offset: 0 });
  cases.push(
    over.status === 'ok' && over.listings.length <= MAX_PAGE_SIZE
      ? pass('60-row server cap holds', `asked for 500, received ${over.listings.length}`)
      : fail('60-row server cap holds', `received ${over.listings.length}`),
  );

  // 4. No protected data in the public payload.
  const leaked = leaks(first.listings);
  cases.push(
    leaked.length === 0
      ? pass('No protected fields in payload', 'no city, zip, owner id, contact or document path on locked leads')
      : fail('No protected fields in payload', `LEAKED: ${leaked.join(', ')}`),
  );

  // 5. Normal browsing must not trip the lockout. The original bug burned a
  //    lockout "strike" on every request, so the 4th call of a session failed.
  const burst: BrowseStatus[] = [];
  for (let i = 0; i < 5; i++) {
    const r = await fetchListingsResult({ limit: 12, offset: 0 });
    burst.push(r.status);
  }
  const blocked = burst.filter((s) => s !== 'ok');
  cases.push(
    blocked.length === 0
      ? pass('Normal session is not rate limited', '6 consecutive requests all returned ok')
      : fail('Normal session is not rate limited', `${blocked.length}/5 calls returned "${blocked[0]}"`),
  );

  // 6. Filtered request still resolves (state filter is server side).
  const filtered = await fetchListingsResult({ state: 'CA', limit: MAX_PAGE_SIZE, offset: 0 });
  cases.push(
    filtered.status === 'ok'
      ? pass('Filtered request resolves', `state=CA returned ${filtered.listings.length} listing(s)`)
      : fail('Filtered request resolves', `status="${filtered.status}"`),
  );

  // 7. Spanish sessions hit the identical endpoint — language must not filter
  //    listings out (listings are stored with their own `language` value and the
  //    feed is language-agnostic by design).
  const langMix = new Set(first.listings.map((l) => l.language).filter(Boolean));
  cases.push(
    pass('Feed is language-agnostic', `languages present in feed: ${[...langMix].join(', ') || 'n/a'} — no client-side language filter is applied`),
  );

  return cases;
};

export const runBrowseRegression = async (): Promise<RegressionCase[]> => {
  const stateCases = runStateMachineCases();
  const liveCases = await runLiveCases();
  return [...stateCases, ...liveCases];
};
